import { useEffect, useRef, useCallback, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';

import { LexGatWordmark } from '../Logo';
import { onboardingClasses, onboardingEase } from '../../lib/onboardingTheme';
import { cn } from '../../lib/utils';

// Future: replace motion wordmark block with <video src="/lexgat-intro.webm" autoPlay muted playsInline />

interface OnboardingWelcomeMomentProps {
  onComplete: () => void;
  displayName?: string;
}

const TIMING = {
  fadeIn: 0.3,
  logoScale: 0.85,
  taglineDelay: 0.55,
  taglineFade: 0.45,
  hold: 0.8,
  fadeOut: 0.5,
} as const;

const REDUCED_MOTION_MS = 600;

export default function OnboardingWelcomeMoment({
  onComplete,
}: OnboardingWelcomeMomentProps) {
  const reducedMotion = useReducedMotion();
  const completedRef = useRef(false);
  const [isExiting, setIsExiting] = useState(false);

  const finish = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  }, [onComplete]);

  const handleSkip = useCallback(() => {
    setIsExiting(true);
    window.setTimeout(finish, reducedMotion ? 50 : TIMING.fadeOut * 1000);
  }, [finish, reducedMotion]);

  useEffect(() => {
    if (reducedMotion) {
      const t = window.setTimeout(finish, REDUCED_MOTION_MS);
      return () => window.clearTimeout(t);
    }

    const holdEndMs =
      (TIMING.fadeIn +
        TIMING.logoScale +
        TIMING.taglineDelay +
        TIMING.taglineFade +
        TIMING.hold) *
      1000;

    const exitTimer = window.setTimeout(() => setIsExiting(true), holdEndMs);
    const completeTimer = window.setTimeout(
      finish,
      holdEndMs + TIMING.fadeOut * 1000,
    );

    return () => {
      window.clearTimeout(exitTimer);
      window.clearTimeout(completeTimer);
    };
  }, [reducedMotion, finish]);

  const ease = onboardingEase;

  return (
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center onboarding-premium-bg onboarding-premium-grain"
      initial={{ opacity: 0 }}
      animate={{ opacity: isExiting ? 0 : 1 }}
      transition={{
        duration: reducedMotion ? 0.01 : isExiting ? TIMING.fadeOut : TIMING.fadeIn,
        ease,
      }}
      role="presentation"
      aria-live="polite"
      aria-label="Welcome to LexGat"
    >
      <div className="onboarding-welcome-vignette absolute inset-0 pointer-events-none" aria-hidden />

      <button
        type="button"
        onClick={handleSkip}
        className="absolute top-6 right-6 md:top-8 md:right-10 z-10 text-[11px] uppercase tracking-[0.2em] text-white/40 hover:text-white/70 transition-colors font-medium"
      >
        Skip
      </button>

      <div className="relative z-10 flex flex-col items-center justify-center px-8 text-center max-w-lg">
        {/* Motion intro — swap this block for video when /lexgat-intro.webm is available */}
        <div className="relative flex items-center justify-center">
          <motion.div
            className={cn(
              'onboarding-welcome-glow absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(100vw,28rem)] h-48 rounded-full pointer-events-none',
              reducedMotion && 'opacity-60',
            )}
            aria-hidden
          />
          <motion.div
            initial={reducedMotion ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.72 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: reducedMotion ? 0.01 : TIMING.logoScale,
              ease,
              delay: reducedMotion ? 0 : TIMING.fadeIn * 0.5,
            }}
          >
            <LexGatWordmark className="h-14 md:h-16 w-auto relative z-10 drop-shadow-[0_0_40px_rgba(232,237,244,0.25)]" />
          </motion.div>
        </div>

        <motion.div
          className="mt-10 md:mt-12 space-y-3"
          initial={reducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: reducedMotion ? 0.01 : TIMING.taglineFade,
            ease,
            delay: reducedMotion ? 0 : TIMING.taglineDelay,
          }}
        >
          <p className={onboardingClasses.microLabel}>Your workspace is ready</p>
          <p className="text-2xl md:text-3xl font-medium tracking-[-0.02em] text-[#F4F6F8]">
            Welcome to LexGat
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
}
